import React from 'react'
import PropTypes from 'prop-types'
import { Box, SvgIcon } from '@mui/material'
import { blue, common } from '@mui/material/colors'
import { useTheme, darken } from '@mui/material/styles'
import RouteAvatar from 'components/route-manager/RouteDetailsModal/RouteAvatar/RouteAvatar'
import { ReactComponent as UnassignedRouteIcon } from 'assets/UnassignedRouteIcon.svg'

const isMuiIcon = Icon => typeof Icon === 'function' && Icon.muiName

/** Work order count badge */
const WorkOrderBadge = ({ count, isInRange }) => {
  const badgeColor = isInRange ? common.white : blue[500]
  const badgeTextColor = isInRange ? blue[500] : common.white

  return (
    <Box
      sx={{
        position: 'relative',
        top: '-2px',
        width: '75%',
        aspectRatio: '1 / 1',
        borderRadius: '50%',
        backgroundColor: badgeColor,
        outline: '2px solid #fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 'clamp(0.4rem, 0.7vw, 0.75rem)',
        fontWeight: 700,
        color: badgeTextColor,
      }}
    >
      {count}
    </Box>
  )
}

WorkOrderBadge.propTypes = {
  count: PropTypes.number,
  isInRange: PropTypes.bool,
}

/** Renders the icon element (MUI or SVG) */
const IconElement = ({ Icon, color, sx }) =>
  isMuiIcon(Icon) ? (
    <Icon sx={{ color, ...sx }} />
  ) : (
    <SvgIcon sx={{ color, ...sx }}>
      <Icon />
    </SvgIcon>
  )

IconElement.propTypes = {
  Icon: PropTypes.elementType,
  color: PropTypes.string,
  sx: PropTypes.object,
}

/** Work order status icon with circular background */
const WorkOrderStatusIcon = ({ Icon, statusColor, isInRange }) => {
  const darkenedColor = darken(statusColor, 0.3)
  const iconColor = isInRange ? darkenedColor : common.white
  const backgroundColor = isInRange ? common.white : darkenedColor

  return (
    <Box
      sx={{
        width: '70%',
        height: 'auto',
        aspectRatio: '1 / 1',
        borderRadius: '50%',
        backgroundColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <IconElement Icon={Icon} color={iconColor} sx={{ fontSize: '70%', width: '70%', height: '70%' }} />
    </Box>
  )
}

WorkOrderStatusIcon.propTypes = {
  Icon: PropTypes.elementType,
  statusColor: PropTypes.string,
  isInRange: PropTypes.bool,
}

/** Service action icon with circular background */
const ServiceActionIcon = ({ Icon, isInRange }) => {
  const theme = useTheme()
  const backgroundColor = isInRange ? common.white : theme.palette.grey[600]
  const iconColor = isInRange ? theme.palette.grey[600] : common.white

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Box
        sx={{
          width: '70%',
          height: 'auto',
          aspectRatio: '1 / 1',
          borderRadius: '50%',
          backgroundColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1px',
        }}
      >
        <IconElement Icon={Icon} color={iconColor} sx={{ fontSize: '70%', width: '70%', height: '70%' }} />
      </Box>
    </Box>
  )
}

ServiceActionIcon.propTypes = {
  Icon: PropTypes.elementType,
  isInRange: PropTypes.bool,
}

/** Route avatar or unassigned route icon */
const RouteIcon = ({ iconData }) => {
  if (iconData.isUnassigned) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '70%',
          height: 'auto',
          aspectRatio: '1 / 1',
        }}
      >
        <SvgIcon sx={{ width: '100%', height: '100%', fontSize: '100%' }}>
          <UnassignedRouteIcon />
        </SvgIcon>
      </Box>
    )
  }

  return (
    <RouteAvatar
      name={iconData.routeName}
      bgcolor={iconData.routeColor}
      sx={{ width: '70%', aspectRatio: '1 / 1', height: 'auto' }}
      TypographyProps={{ fontSize: 'clamp(0.5rem, 0.5vw, 0.65rem)' }}
    />
  )
}

RouteIcon.propTypes = {
  iconData: PropTypes.shape({
    isUnassigned: PropTypes.bool,
    routeName: PropTypes.string,
    routeColor: PropTypes.string,
  }),
}

/** Standard icon (MUI or SVG) */
const StandardIcon = ({ Icon, color }) => (
  <IconElement Icon={Icon} color={color} sx={isMuiIcon(Icon) ? { fontSize: '85%' } : { fontSize: '100%', width: '100%', height: '100%' }} />
)

StandardIcon.propTypes = {
  Icon: PropTypes.elementType,
  color: PropTypes.string,
}

/**
 * Renders icon content for a calendar day cell
 * Handles different icon types: work orders, service actions, routes, and standard icons
 */
const CalendarDayIcon = ({ label, iconData, isRangeStart, isRangeEnd }) => {
  const { Icon, color, count, statusColor } = iconData
  const isInRange = isRangeStart || isRangeEnd

  switch (label) {
    case 'WorkOrders':
      return <WorkOrderBadge count={count} isInRange={isInRange} />
    case 'WorkOrderStatus':
      return <WorkOrderStatusIcon Icon={Icon} statusColor={statusColor} isInRange={isInRange} />
    case 'ServiceAction':
      return <ServiceActionIcon Icon={Icon} isInRange={isInRange} />
    case 'Routes':
      return <RouteIcon iconData={iconData} />
    default:
      return <StandardIcon Icon={Icon} color={color} />
  }
}

CalendarDayIcon.propTypes = {
  label: PropTypes.string.isRequired,
  iconData: PropTypes.shape({
    Icon: PropTypes.elementType,
    color: PropTypes.string,
    count: PropTypes.number,
    routeName: PropTypes.string,
    routeColor: PropTypes.string,
    isUnassigned: PropTypes.bool,
    statusColor: PropTypes.string,
  }).isRequired,
  isRangeStart: PropTypes.bool,
  isRangeEnd: PropTypes.bool,
}

export default CalendarDayIcon
